import {
  KlipyGifModel,
  KlipyGifSelection,
  useKlipyGifs,
} from "@jugl-web/rest-api";
import { LoadingSpinner, PlainButton, SearchInput } from "@jugl-web/ui-kit";
import { useDebounce, useTranslations } from "@jugl-web/utils";
import { environment } from "@web-src/environments/environment";
import { selectUserId } from "@web-src/modules/auth/store/authSlice";
import React, { useCallback, useEffect, useState } from "react";
import { useInView } from "react-intersection-observer";
import { useSelector } from "react-redux";

export interface KlipyGifPickerProps {
  onGifSelect: (gif: KlipyGifSelection) => void;
}

export const KlipyGifPicker: React.FC<KlipyGifPickerProps> = ({
  onGifSelect,
}) => {
  const { t } = useTranslations();
  const customerId = useSelector(selectUserId) || "";
  const [searchValue, setSearchValue] = useState("");
  const debouncedSearchValue = useDebounce(searchValue) as string;

  const {
    data: items,
    hasMore: hasNext,
    isLoading,
    isLoadingMore,
    isError,
    isLoadingMoreError,
    loadMore,
    retry,
  } = useKlipyGifs({
    apiKey: environment.klipyApiKey,
    customerId,
    query: debouncedSearchValue,
  });

  const { ref: loadMoreRef, inView: isLoadMoreInView } = useInView({
    rootMargin: "120px",
  });

  useEffect(() => {
    // `isError` stops the sentinel from re-firing failed requests on every render
    if (
      !isLoadMoreInView ||
      !hasNext ||
      isLoading ||
      isLoadingMore ||
      isError
    ) {
      return;
    }
    loadMore();
  }, [hasNext, isLoadMoreInView, isLoading, isLoadingMore, isError, loadMore]);

  const handleSelect = useCallback(
    (item: KlipyGifModel) => {
      onGifSelect({
        url: item.send.url,
        width: item.send.width,
        height: item.send.height,
        size: item.send.size,
      });
    },
    [onGifSelect]
  );

  const showInitialLoading = isLoading && items.length === 0;

  return (
    <div className="flex h-[390px] w-[352px] flex-col overflow-hidden rounded-xl border border-solid border-grey-200 bg-white shadow-lg">
      <div className="border-0 border-b border-solid border-grey-200 p-3">
        <SearchInput
          variant="filled"
          color="grey"
          size="sm"
          value={searchValue}
          placeholder={t({
            id: "chats-page.search-klipy",
            defaultMessage: "Search KLIPY",
          })}
          onChange={(e) => setSearchValue(e.target.value.trimStart())}
          onClear={() => setSearchValue("")}
        />
      </div>

      <div className="relative min-h-0 flex-1 overflow-y-auto p-3">
        {showInitialLoading && (
          <div className="flex h-full items-center justify-center">
            <LoadingSpinner />
          </div>
        )}

        {!showInitialLoading && isError && items.length === 0 && (
          <div className="flex h-full items-center justify-center px-4 text-center font-secondary text-sm text-grey-800">
            {t({
              id: "common.something-went-wrong",
              defaultMessage: "Something went wrong",
            })}
          </div>
        )}

        {!showInitialLoading && !isError && items.length === 0 && (
          <div className="flex h-full items-center justify-center px-4 text-center font-secondary text-sm text-grey-800">
            {t({
              id: "chats-page.no-gifs-found",
              defaultMessage: "No GIFs found",
            })}
          </div>
        )}

        {items.length > 0 && (
          <div className="grid grid-cols-3 gap-2">
            {items.map((item) => (
              <PlainButton
                key={item.id}
                className="relative aspect-square overflow-hidden rounded-md bg-grey-100"
                onClick={() => handleSelect(item)}
              >
                <img
                  src={item.previewUrl}
                  alt={item.title}
                  loading="lazy"
                  className="h-full w-full object-cover"
                />
              </PlainButton>
            ))}
          </div>
        )}

        {hasNext && (
          <div ref={loadMoreRef} className="flex justify-center py-3">
            {isLoadingMore && <LoadingSpinner size="sm" />}
            {isLoadingMoreError && !isLoadingMore && (
              <PlainButton
                className="font-secondary text-sm font-medium text-primary-500"
                onClick={retry}
              >
                {t({ id: "common.retry", defaultMessage: "Retry" })}
              </PlainButton>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
